/**
 * ./exercise5/Exercise5Workout.js
 *
 * This module **guides users through Exercise 5**, which focuses on **scrambled sentence reconstruction**
 * while maintaining **rhythmic breathing**. The exercise includes:
 * - **Scrambled sentences for mental agility**
 *   - Randomly selects **common proverbs & sayings**.
 *   - **Shuffles the words** in each sentence for challenge.
 *   - **Updates sentences** in sync with **breathing cycles**.
 * - **Timed sentence updates synchronized with breathing**
 * - **Live session tracking & progress monitoring**
 * - **Auto-completion & navigation to the test phase**
 * 
 */


import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Exercise5Workout.css'; // Importing the CSS file for styling
import Circle1 from '../components/icons/circlein1.svg';
import Circle2 from '../components/icons/circlein2.svg';
import Circle3 from '../components/icons/circlein3.svg';
import Circle4 from '../components/icons/circlein4.svg';
import Circle5 from '../components/icons/circlein5.svg';
import Circle6 from '../components/icons/circlein6.svg';
import Circle7 from '../components/icons/circlein7.svg';

import stick1 from "../components/icons/stick1.svg";
import stick2 from "../components/icons/stick2.svg";
import stick3 from "../components/icons/stick3.svg";
import stick4 from "../components/icons/stick4.svg";
import stick5 from "../components/icons/stick5.svg";
import stick6 from "../components/icons/stick6.svg";
import stick7 from "../components/icons/stick7.svg";

import CloseIcon from '../components/icons/close.svg';
import InfoIcon from '../components/icons/InfoIcon.svg'; 
import PlayIcon from '../components/icons/play.svg';
import StopIcon from '../components/icons/stop.svg';
import Curtain from '../components/Curtain';
import person1 from '../components/icons/person1.svg';
import person2 from '../components/icons/person2.svg';
import person3 from '../components/icons/person3.svg';
import person4 from '../components/icons/person4.svg';
import person5 from '../components/icons/person5.svg';
import person6 from '../components/icons/person6.svg';
import person7 from '../components/icons/person7.svg';
import { DEBUG_TIME } from "../config";

function Exercise5Workout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [long, setLong] = useState(3.0);
  const [short, setShort] = useState(2.0);
  const [hold, setHold] = useState(1.0);
  const [hasStartedOnce, setHasStartedOnce] = useState(false);
  const [wordChangeInterval, setWordChangeInterval] = useState(1000); //speed of word change
  const duration = location.state?.duration || 3; // Default to 3 minutes if not specified
  const initialDuration = duration * 60; // Convert minutes to seconds
  const timeRef = useRef(0); // Add this for time tracking
  const [showPlayIcon, setShowPlayIcon] = useState(true);
  const [showStopIcon, setShowStopIcon] = useState(false);
  const [instructionText, setInstructionText] = useState('Play');
  const [isTransitioningToOne, setIsTransitioningToOne] = useState(false);
  const [showCurtain, setShowCurtain] = useState(false);


  useEffect(() => {
    // Load values from localStorage
    setLong(parseFloat(localStorage.getItem('long')) || 2.0);
    setShort(parseFloat(localStorage.getItem('short')) || 1.0);
    setHold(parseFloat(localStorage.getItem('hold')) || 1.0);
  }, []);

  const [time, setTime] = useState(location.state?.time ?? initialDuration);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(1);
  const [nextStep, setNextStep] = useState(2);
  const [previousStep, setPreviousStep] = useState(null);
  const [fadeInStyle, setFadeInStyle] = useState({ opacity: 1 });
  const [fadeOutStyle, setFadeOutStyle] = useState({ opacity: 0 });
  const [sentences, setSentences] = useState([]);
  const [currentSentence, setCurrentSentence] = useState('Play');
  const [sentenceIndex, setSentenceIndex] = useState(0);
  const [sentenceChangeInterval] = useState(5000); // 5 seconds for each sentence
  const [isLoading, setIsLoading] = useState(true);
  const [displayedSentences, setDisplayedSentences] = useState([]);
  const [isPressed, setIsPressed] = useState(false);
  const [errorRate, setErrorRate] = useState(0);

  const shuffleArray = (array) => {
    return array.sort(() => Math.random() - 0.5);
  };

  // Replace the generateScrambledSentences function with this:
  const getSentences = () => {
    return [
      "The early bird catches the worm.",
      "Actions speak louder than words.",
      "A picture is worth a thousand words.",
      "When in Rome, do as the Romans do.",
      "The pen is mightier than the sword.",
      "Knowledge is power, guard it well.",
      "Time waits for no one, act now.",
      "Opportunity knocks but once, seize it.",
      "Fortune favors the bold, take risks.",
      "A journey of a thousand miles begins with a single step.",
      "Practice makes perfect, keep trying.",
      "The grass is always greener on the other side.",
      "Don't count your chickens before they hatch.",
      "A watched pot never boils, be patient.",
      "Birds of a feather flock together.",
      "Absence makes the heart grow fonder.",
      "Beauty is in the eye of the beholder.",
      "Better late than never, don't rush.",
      "Brevity is the soul of wit.",
      "Cleanliness is next to godliness.",
      "Curiosity killed the cat, be cautious.",
      "Discretion is the better part of valor.",
      "Don't bite the hand that feeds you.",
      "Don't put all your eggs in one basket.",
      "Every cloud has a silver lining.",
      "Familiarity breeds contempt, keep distance.",
      "Fortune favors the prepared mind.",
      "Good things come to those who wait.",
      "Haste makes waste, slow down.",
      "Honesty is the best policy, be truthful.",
      "Hope for the best, prepare for the worst.",
      "If it ain't broke, don't fix it.",
      "If you can't beat them, join them.",
      "Ignorance is bliss, stay unaware.",
      "Imitation is the sincerest form of flattery.",
      "In the land of the blind, the one-eyed man is king.",
      "It takes two to tango, cooperate.",
      "Keep your friends close and your enemies closer.",
      "Laughter is the best medicine, stay happy.",
      "Let sleeping dogs lie, avoid trouble.",
      "Look before you leap, be cautious.",
      "Love is blind, be careful.",
      "Money can't buy happiness, value life.",
      "Necessity is the mother of invention.",
      "No man is an island, seek help.",
      "Old habits die hard, change slowly.",
      "One man's trash is another man's treasure.",
      "Out of sight, out of mind.",
      "Patience is a virtue, wait calmly.",
      "People who live in glass houses shouldn't throw stones.",
      "Practice what you preach, be consistent.",
      "Rome wasn't built in a day, be patient.",
      "Silence is golden, speak less.",
      "The best things in life are free.",
      "The customer is always right, listen carefully.",
      "The early bird gets the worm, act quickly.",
      "The ends justify the means, be pragmatic.",
      "The grass is always greener on the other side.",
      "The more the merrier, invite friends.",
      "The pen is mightier than the sword.",
      "The proof of the pudding is in the eating.",
      "The road to hell is paved with good intentions.",
      "The squeaky wheel gets the grease.",
      "There's no place like home, cherish it.",
      "There's no such thing as a free lunch.",
      "Time flies when you're having fun.",
      "To err is human, to forgive divine.",
      "Too many cooks spoil the broth.",
      "Two heads are better than one.",
      "Variety is the spice of life.",
      "What goes around comes around.",
      "When in doubt, leave it out.",
      "Where there's a will, there's a way.",
      "You can't have your cake and eat it too.",
      "You can't judge a book by its cover.",
      "You can't make an omelet without breaking eggs.",
      "You can't teach an old dog new tricks.",
      "You reap what you sow, be careful.",
      "Your guess is as good as mine.",
      "A bird in the hand is worth two in the bush.",
      "A chain is only as strong as its weakest link.",
      "A fool and his money are soon parted.",
      "A friend in need is a friend indeed.",
      "A leopard can't change its spots.",
      "A penny saved is a penny earned.",
      "A rolling stone gathers no moss.",
      "A stitch in time saves nine.",
      "All good things must come to an end.",
      "All that glitters is not gold.",
      "An apple a day keeps the doctor away.",
      "An ounce of prevention is worth a pound of cure.",
      "As you sow, so shall you reap.",
      "Beggars can't be choosers, accept what you get.",
      "Better safe than sorry, take precautions.",
      "Blood is thicker than water, value family.",
      "Charity begins at home, start with yourself.",
      "Don't cry over spilled milk, move on.",
      "Don't judge a book by its cover.",
      "Don't look a gift horse in the mouth.",
      "Don't put off until tomorrow what you can do today.",
      "Early to bed and early to rise makes a man healthy, wealthy, and wise.",
      "Every dog has its day, be patient.",
      "Fools rush in where angels fear to tread.",
      "Give a man a fish and you feed him for a day.",
      "Give credit where credit is due.",
      "God helps those who help themselves.",
      "Good fences make good neighbors.",
      "He who hesitates is lost, act quickly.",
      "He who laughs last laughs best.",
      "He who lives by the sword dies by the sword.",
      "He who pays the piper calls the tune.",
      "Home is where the heart is, cherish it.",
      "If at first you don't succeed, try, try again.",
      "If you can't stand the heat, get out of the kitchen.",
      "If you want something done right, do it yourself.",
      "In for a penny, in for a pound.",
      "It is better to give than to receive.",
      "It takes a village to raise a child.",
      "It's always darkest before the dawn.",
      "It's better to have loved and lost than never to have loved at all.",
      "It's never too late to learn.",
      "It's no use crying over spilled milk.",
      "Jack of all trades, master of none.",
      "Keep your chin up, stay positive.",
      "Knowledge is power, seek it.",
      "Lend your money and lose your friend.",
      "Let bygones be bygones, forgive and forget.",
      "Life is a journey, not a destination.",
      "Lightning never strikes twice in the same place.",
      "Live and let live, tolerate others.",
      "Look before you leap, be cautious.",
      "Love conquers all, believe in it.",
      "Make hay while the sun shines.",
      "Many hands make light work.",
      "Money doesn't grow on trees, spend wisely.",
      "Necessity is the mother of invention.",
      "No news is good news, stay calm.",
      "No pain, no gain, work hard.",
      "Nothing ventured, nothing gained, take risks.",
      "Old habits die hard, change slowly.",
      "One good turn deserves another.",
      "One man's meat is another man's poison.",
      "Out of sight, out of mind.",
      "Patience is a virtue, wait calmly.",
      "People who live in glass houses shouldn't throw stones.",
      "Practice what you preach, be consistent.",
      "Rome wasn't built in a day, be patient.",
      "Silence is golden, speak less.",
      "The best things in life are free.",
      "The customer is always right, listen carefully.",
      "The early bird gets the worm, act quickly.",
      "The ends justify the means, be pragmatic.",
      "The grass is always greener on the other side.",
      "The more the merrier, invite friends.",
      "The pen is mightier than the sword.",
      "The proof of the pudding is in the eating.",
      "The road to hell is paved with good intentions.",
      "The squeaky wheel gets the grease.",
      "There's no place like home, cherish it.",
      "There's no such thing as a free lunch.",
      "Time flies when you're having fun.",
      "To err is human, to forgive divine.",
      "Too many cooks spoil the broth.",
      "Two heads are better than one.",
      "Variety is the spice of life.",
      "What goes around comes around.",
      "When in doubt, leave it out.",
      "When the going gets tough, the tough get going.",
      "Where there's a will, there's a way.",
      "You can't have your cake and eat it too.",
      "You can't judge a book by its cover.",
      "You can't make an omelet without breaking eggs.",
      "You can't teach an old dog new tricks.",
      "You reap what you sow, be careful.",
      "Your guess is as good as mine.",
      "Birds sing sweetly in the early morning light.",
      "Trees sway gently in the summer breeze.",
      "Rivers carve paths through the ancient mountains.",
      "Cats purr contentedly by the warm fireplace.",
      "Dogs bark loudly at the passing strangers.",
      "Foxes dart quickly through the dense underbrush.",
      "Pencils scribble notes on the crisp white paper.",
      "Apples fall silently from the old orchard trees.",
      "Books open doors to worlds unknown and unseen.",
      "Cars zoom past on the busy city streets.",
      "Clouds drift lazily across the bright blue sky.",
      "Flowers bloom brightly in the springtime garden.",
      "Stars twinkle softly in the vast night sky.",
      "Waves crash loudly against the rocky shoreline.",
      "Leaves rustle quietly in the gentle autumn wind.",
      "Mountains stand tall against the clear horizon.",
      "Sunsets paint the sky with vibrant hues.",
      "Rain falls gently on the thirsty earth.",
      "Snow blankets the ground in a soft white layer.",
      "Wind whispers secrets through the tall grass.",
      "Fire crackles warmly in the cozy hearth.",
      "Ice glistens brightly in the morning sun.",
      "Thunder rumbles ominously in the distant sky.",
      "Lightning flashes brightly in the stormy night.",
      "Oceans stretch endlessly beyond the sandy beaches.",
      "Deserts hold mysteries beneath the scorching sun.",
      "Forests teem with life in every corner.",
      "Valleys nestle quietly between towering peaks.",
      "Lakes reflect the beauty of the surrounding landscape.",
      "Rocks stand firm against the test of time.",
      "Paths wind through the lush green meadows.",
      "Bridges span the wide and rushing rivers.",
      "Caves hide secrets deep within their dark recesses.",
      "Fields stretch endlessly under the open sky.",
      "Gardens burst with color in the warm sunlight.",
      "Hills roll gently across the peaceful countryside.",
      "Islands dot the vast and open sea.",
      "Jungles teem with life in every direction.",
      "Meadows bloom with wildflowers in the springtime.",
      "Plains stretch wide under the endless sky.",
      "Ridges rise sharply against the clear blue sky.",
      "Springs bubble up from the hidden depths.",
      "Tides ebb and flow with the pull of the moon.",
      "Valleys echo with the calls of distant birds.",
      "Waterfalls cascade down the steep rocky cliffs.",
      "Xylophones play softly in the gentle breeze.",
      "Yachts sail gracefully across the calm waters.",
      "Zebras graze peacefully on the open savannah.",
      "Time flies when you're having fun.",
      "A friend in need is a friend indeed.",
      "The grass is always greener on the other side.",
      "Better late than never.",
      "Don't count your chickens before they hatch.",
      "Every cloud has a silver lining.",
      "Two wrongs don’t make a right.",
      "Actions speak louder than words.",
      "A penny saved is a penny earned.",
      "You can’t judge a book by its cover.",
      "What goes around comes around.",
      "Don’t bite the hand that feeds you.",
      "No pain, no gain.",
      "Look before you leap.",
      "Don’t put all your eggs in one basket.",
      "It’s no use crying over spilled milk.",
      "You reap what you sow.",
      "The early bird catches the worm.",
      "Laughter is the best medicine.",
      "Honesty is the best policy.",
      "Knowledge is power.",
      "A bird in the hand is worth two in the bush.",
      "Don’t burn your bridges.",
      "Rome wasn’t built in a day.",
      "Jack of all trades, master of none.",
      "Don’t make a mountain out of a molehill.",
      "A watched pot never boils.",
      "An apple a day keeps the doctor away.",
      "The squeaky wheel gets the grease.",
      "Beauty is in the eye of the beholder.",
      "The pen is mightier than the sword.",
      "What’s done is done.",
      "Actions have consequences.",
      "It’s better to be safe than sorry.",
      "A rolling stone gathers no moss.",
      "Blood is thicker than water.",
      "Do unto others as you would have them do unto you.",
      "Out of sight, out of mind.",
      "You can’t have your cake and eat it too.",
      "There’s no place like home.",
      "It takes two to tango.",
      "Time is money.",
      "Too many cooks spoil the broth.",
      "Don’t cry over spilled milk.",
      "Strike while the iron is hot.",
      "Absence makes the heart grow fonder.",
      "The road to hell is paved with good intentions.",
      "Don’t let the cat out of the bag.",
      "A fool and his money are soon parted.",
      "Curiosity killed the cat.",
      "Actions speak louder than words.",
      "Still waters run deep.",
      "If it ain’t broke, don’t fix it.",
      "The best things in life are free.",
      "You can’t please everyone.",
      "A chain is only as strong as its weakest link.",
      "You can lead a horse to water, but you can’t make it drink.",
      "There’s no time like the present.",
      "Don’t judge a book by its cover.",
      "Where there’s smoke, there’s fire.",
      "Many hands make light work.",
      "Necessity is the mother of invention.",
      "One man’s trash is another man’s treasure.",
      "All that glitters is not gold.",
      "A stitch in time saves nine.",
      "A house divided against itself cannot stand.",
      "The truth will set you free.",
      "Nothing ventured, nothing gained.",
      "Live and let live.",
      "What doesn’t kill you makes you stronger.",
      "Don’t count your chickens before they hatch.",
      "The truth is always simple.",
      "The customer is always right.",
      "It’s not over until it’s over.",
      "You can't teach an old dog new tricks.",
      "The best way to predict the future is to create it.",
      "Time heals all wounds.",
      "In the land of the blind, the one-eyed man is king.",
      "You miss 100% of the shots you don’t take.",
      "Better safe than sorry.",
      "When the going gets tough, the tough get going.",
      "Don’t put off until tomorrow what you can do today.",
      "The bigger they are, the harder they fall.",
      "If you want something done right, do it yourself.",
      "Every dog has its day.",
      "If you can't beat them, join them.",
      "Patience is a virtue.",
      "Don’t shoot the messenger.",
      "All good things must come to an end.",
      "It’s the thought that counts.",
      "Life is like a box of chocolates.",
      "Everything happens for a reason.",
      "Every rose has its thorn.",
      "You can’t change the past.",
      "A picture is worth a thousand words.",
      "Don’t put all your eggs in one basket.",
      "Absence makes the heart grow fonder.",
      "A watched pot never boils.",
      "If you don’t try, you’ll never know.",
      "It’s never too late to start.",
      "When in Rome, do as the Romans do.",
      "The pen is mightier than the sword.",
      "A penny for your thoughts.",
      "The grass is always greener on the other side.",
      "Good things come to those who wait.",
      "Don’t bite the hand that feeds you.",
      "You can't always get what you want.",
      "What goes around, comes around.",
      "The early bird gets the worm.",
      "Don’t judge a book by its cover.",
      "Actions speak louder than words.",
      "The road to success is always under construction.",
      "What doesn’t kill you makes you stronger.",
      "Don’t put off until tomorrow what you can do today.",
      "A house divided cannot stand.",
      "Too many cooks spoil the broth.",
      "Practice makes perfect.",
      "Don’t make a mountain out of a molehill.",
      "It takes one to know one.",
      "You can't make an omelet without breaking eggs.",
      "Live for today, plan for tomorrow.",
      "The more the merrier.",
      "Don’t burn your bridges.",
      "The squeaky wheel gets the grease.",
      "An ounce of prevention is worth a pound of cure.",
      "You can’t judge a book by its cover.",
      "The early bird catches the worm.",
      "Make hay while the sun shines.",
      "Don’t throw in the towel.",
      "Jack of all trades, master of none.",
      "Don’t make promises you can’t keep.",
      "A penny saved is a penny earned.",
      "It’s never too late to change.",
      "Beauty is only skin deep.",
      "The world is your oyster.",
      "No man is an island.",
      "Too much of anything is bad.",
      "Don’t bite off more than you can chew.",
      "A rolling stone gathers no moss.",
      "The best things in life are free.",
      "Let bygones be bygones.",
      "If the shoe fits, wear it.",
      "Nothing lasts forever.",
      "A fool and his money are soon parted.",
      "A bird in the hand is worth two in the bush.",
      "Keep your friends close, and your enemies closer.",
      "What doesn’t kill you makes you stronger.",
      "Money can’t buy happiness.",
      "A penny for your thoughts.",
      "Don’t count your chickens before they hatch.",
      "A little knowledge is a dangerous thing.",
      "When life gives you lemons, make lemonade.",
      "You can’t have your cake and eat it too.",
      "Silence is golden.",
      "It’s always darkest before the dawn.",
      "What’s in a name?",
      "Don’t rock the boat.",
      "You can’t please everyone.",
      "Out of sight, out of mind.",
      "Time waits for no one.",
      "It takes two to tango.",
      "Love is blind.",
      "Don’t put off until tomorrow what you can do today.",
      "One step at a time.",
      "Don’t cry over spilled milk.",
      "Knowledge is power.",
      "You reap what you sow.",
      "Time is money.",
      "The truth will set you free.",
      "A fool’s paradise is a wise man’s torment.",
      "What you see is what you get.",
      "Life goes on.",
      "A chain is only as strong as its weakest link.",
      "You can’t teach an old dog new tricks.",
      "All things come to those who wait.",
      "Don’t judge a book by its cover.",
      "The truth is always simple.",
      "Every rose has its thorn.",
      "Live and let live.",
      "Two heads are better than one.",
      "Love makes the world go round.",
      "Money talks.",
      "He who laughs last, laughs best.",
      "It’s better to give than receive.",
      "A stitch in time saves nine.",
      "Curiosity killed the cat.",
      "Out of the frying pan and into the fire.",
      "A fool and his money are soon parted.",
      "When it rains, it pours.",
      "A house divided against itself cannot stand.",
      "All’s well that ends well.",
      "A penny saved is a penny earned.",
      "A good deed is its own reward.",
      "A watched pot never boils.",
      "No news is good news.",
      "A picture is worth a thousand words.",
      "Live for today, plan for tomorrow.",
      "Look before you leap.",
      "If you don’t ask, you don’t get.",
      "A journey of a thousand miles begins with one step.",
      "A bird in the hand is worth two in the bush.",
      "The best things in life are free.",
      "You can lead a horse to water, but you can’t make it drink.",
      "Good things come to those who wait.",
      "Better late than never.",
      "There’s no place like home.",
      "What goes around comes around.",
      "A penny saved is a penny earned.",
      "When the cat’s away, the mice will play.",
      "Beauty is in the eye of the beholder.",
      "Time flies when you’re having fun.",
      "You can’t make everyone happy.",
      "Don’t bite the hand that feeds you.",
      "No man is an island.",
      "An ounce of prevention is worth a pound of cure.",
      "Live and let live.",
      "Out of sight, out of mind.",
      "You miss 100% of the shots you don’t take.",
      "What doesn’t kill you makes you stronger.",
      "Money can’t buy happiness.",
      "The best is yet to come.",
      "Happiness is a choice.",
      "Silence is golden.",
      "The early bird catches the worm.",
      "The grass is always greener on the other side.",
      "Don’t throw stones in a glass house.",
      "The pen is mightier than the sword.",
      "You can’t judge a book by its cover.",
      "There’s no time like the present.",
      "Every dog has its day.",
      "Patience is a virtue.",
      "What goes up must come down.",
      "An apple a day keeps the doctor away.",
      "You reap what you sow.",
      "There’s no place like home.",
      "Don’t count your chickens before they hatch.",
      "What doesn’t kill you makes you stronger.",
      "Don’t burn your bridges.",
      "The road to success is always under construction.",
      "Don’t put off until tomorrow what you can do today.",
      "Too many cooks spoil the broth.",
      "Life is like a box of chocolates.",
      "Actions speak louder than words.",
      "If the shoe fits, wear it.",
      "Don’t judge a book by its cover.",
      "Every rose has its thorn.",
      "You can’t please everyone.",
      "A fool’s paradise is a wise man’s torment.",
      "Still waters run deep.",
      "The truth will set you free.",
      "Don’t make a mountain out of a molehill.",
      "A bird in the hand is worth two in the bush.",
      "The best way to predict the future is to create it.",
      "You can't teach an old dog new tricks.",
      "Life goes on.",
      "If you want something done right, do it yourself.",
      "You can’t make an omelet without breaking eggs.",
      "Better safe than sorry.",
      "You can’t always get what you want.",
      "Two heads are better than one.",
      "A penny saved is a penny earned.",
      "Time is money.",
      "Laughter is the best medicine.",
      "A fool and his money are soon parted.",
      "Better late than never.",
      "The early bird catches the worm.",
      "Patience is a virtue.",
      "Don’t cry over spilled milk.",
      "Good things come to those who wait.",
      "The truth will set you free.",
      "Every dog has its day.",
      "Live for today, plan for tomorrow.",
      "Don’t count your chickens before they hatch.",
      "It takes one to know one.",
      "You can’t have your cake and eat it too.",
      "Silence is golden.",
      "Nothing ventured, nothing gained.",
      "Love is blind.",
      "Don’t shoot the messenger.",
      "What doesn’t kill you makes you stronger.",
      "A stitch in time saves nine.",
      "Don’t make promises you can’t keep.",
      "Better safe than sorry.",
      "The more the merrier.",
      "You reap what you sow.",
      "The grass is always greener on the other side.",
      "When in Rome, do as the Romans do.",
      "Every cloud has a silver lining.",
      "Too much of anything is bad.",
      "Live and let live.",
      "Still waters run deep.",
      "An ounce of prevention is worth a pound of cure.",
      "The best is yet to come.",
      "A chain is only as strong as its weakest link.",
      "You can lead a horse to water, but you can’t make it drink.",
      "Better late than never.",
      "Don’t bite the hand that feeds you.",
      "It’s better to be safe than sorry.",
      "Time flies when you’re having fun.",
      "Good things come to those who wait.",
      "You can’t teach an old dog new tricks.",
      "Where there’s smoke, there’s fire.",
      "Live and let live.",
      "Actions speak louder than words.",
      "A penny for your thoughts.",
      "The best things in life are free.",
      "The early bird gets the worm.",
      "Look before you leap.",
      "Silence is golden.",
      "It’s never too late to change.",
      "The truth will set you free.",
      "What goes around comes around.",
      "When life gives you lemons, make lemonade.",
      "A picture is worth a thousand words.",
      "There’s no place like home.",
      "If you can’t beat them, join them.",
      "The pen is mightier than the sword.",
      "Still waters run deep.",
      "The squeaky wheel gets the grease.",
      "Patience is a virtue.",
      "A fool and his money are soon parted.",
      "Don’t make a mountain out of a molehill.",
      "Better safe than sorry.",
      "You can’t please everyone.",
      "You can’t judge a book by its cover.",
      "The truth will set you free.",
      "Live for today, plan for tomorrow.",
      "Don’t cry over spilled milk.",
      "What doesn’t kill you makes you stronger.",
      "It takes one to know one.",
      "Live and let live.",
      "Time is money.",
      "You can’t have your cake and eat it too.",
      "What goes up must come down.",
      "Still waters run deep.",
      "Better late than never.",
      "Good things come to those who wait.",
      "A bird in the hand is worth two in the bush.",
      "Life goes on.",
      "You can't make everyone happy.",
      "Out of sight, out of mind.",
      "Two heads are better than one.",
      "Patience is a virtue.",
      "Better safe than sorry.",
      "What you see is what you get.",
      "Nothing lasts forever.",
      "You reap what you sow.",
      "Time heals all wounds.",
      "Knowledge is power.",
      "It’s the thought that counts.",
      "Still waters run deep.",
      "A watched pot never boils.",
      "All good things must come to an end.",
      "Better safe than sorry.",
      "Live for today, plan for tomorrow.",
      "The best things in life are free.",
      "Don’t judge a book by its cover.",
      "The early bird gets the worm.",
      "A chain is only as strong as its weakest link.",
      "Better late than never.",
      "What doesn’t kill you makes you stronger.",
      "Silence is golden.",
      "Nothing ventured, nothing gained.",
      "A penny for your thoughts.",
      "Time is money.",
      "Good things come to those who wait.",
      "You can’t teach an old dog new tricks.",
      "What goes around, comes around.",
      "The pen is mightier than the sword.",
      "Better safe than sorry.",
      "Actions speak louder than words.",
      "You can’t have your cake and eat it too.",
      "Where there’s smoke, there’s fire.",
      "What you see is what you get.",
      "A rolling stone gathers no moss.",
      "When life gives you lemons, make lemonade.",
      "All’s fair in love and war.",
      "Live for today, plan for tomorrow.",
      "You reap what you sow.",
      "There’s no place like home.",
      "Look before you leap.",
      "Every dog has its day.",
      "The truth will set you free.",
      "An apple a day keeps the doctor away.",
      "Too much of anything is bad.",
      "Better late than never.",
      "A stitch in time saves nine.",
      "A penny saved is a penny earned.",
      "It’s never too late to start.",
      "You can't please everyone.",
      "Still waters run deep.",
      "Every cloud has a silver lining.",
      "You can’t make everyone happy.",
      "A friend in need is a friend indeed.",
      "The truth is always simple.",
      "A rolling stone gathers no moss.",
      "What goes around, comes around.",
      "Actions speak louder than words.",
      "Better safe than sorry.",
      "What doesn’t kill you makes you stronger.",
      "Time flies when you’re having fun.",
      "All that glitters is not gold.",
      "A chain is only as strong as its weakest link.",
      "An apple a day keeps the doctor away.",
      "Too many cooks spoil the broth.",
      "Silence is golden.",
      "You can’t teach an old dog new tricks.",
      "Better late than never.",
      "What you see is what you get.",
      "Love is blind.",
      "Live for today, plan for tomorrow.",
      "Patience is a virtue.",
      "The early bird catches the worm.",
      "You reap what you sow.",
      "Actions speak louder than words.",
      "You can’t judge a book by its cover.",
      "Look before you leap.",
      "Don’t bite the hand that feeds you.",
      "Time is money.",
      "Too much of anything is bad.",
      "Silence is golden.",
      "The best things in life are free.",
      "Don’t cry over spilled milk.",
      "A stitch in time saves nine.",
      "Nothing ventured, nothing gained.",
      "Still waters run deep.",
      "You can’t have your cake and eat it too.",
      "Better late than never.",
      "Every cloud has a silver lining.",
      "Better safe than sorry.",
      "A bird in the hand is worth two in the bush.",
      "Time waits for no one.",
      "A picture is worth a thousand words.",
      "Look before you leap.",
      "A friend in need is a friend indeed.",
      "Don’t judge a book by its cover.",
      "Too many cooks spoil the broth.",
      "The early bird catches the worm.",
      "You can’t please everyone.",
      "Patience is a virtue.",
      "Still waters run deep.",
      "Every dog has its day.",
      "You can’t have your cake and eat it too.",
      "A rolling stone gathers no moss.",
      "Nothing ventured, nothing gained.",
      "Good things come to those who wait.",
      "What goes around comes around.",
      "Live for today, plan for tomorrow.",
      "You can’t teach an old dog new tricks.",
      "A penny saved is a penny earned.",
      "Better late than never.",
      "Time heals all wounds.",
      "Life is what you make it.",
      "The road to success is always under construction.",
      "Better safe than sorry.",
      "Actions speak louder than words.",
      "What doesn’t kill you makes you stronger.",
      "The truth will set you free.",
      "A fool and his money are soon parted.",
      "Life is what you make it.",
      "Nothing lasts forever.",
      "Every rose has its thorn.",
      "Better safe than sorry.",
      "Time flies when you’re having fun.",
      "You can't have your cake and eat it too.",
      "What goes up must come down.",
      "The truth will set you free.",
      "Still waters run deep.",
      "What you see is what you get.",
      "You reap what you sow.",
      "The best things in life are free.",
      "You can’t teach an old dog new tricks.",
      "Better safe than sorry.",
      "When the cat’s away, the mice will play.",
      "A fool and his money are soon parted.",
      "Love is blind.",
      "A picture is worth a thousand words.",
      "A friend in need is a friend indeed.",
      "When life gives you lemons, make lemonade.",
      "What goes around comes around.",
      "Better late than never.",
      "The early bird catches the worm.",
      "Too much of anything is bad.",
      "Don’t make a mountain out of a molehill.",
      "What doesn’t kill you makes you stronger.",
      "The road to success is always under construction.",
      "Time is money.",
      "Every dog has its day.",
      "You can't please everyone.",
      "Actions speak louder than words.",
      "Don’t cry over spilled milk.",
      "The truth will set you free.",
      "You reap what you sow.",
      "Patience is a virtue.",
      "Better late than never.",
      "Live for today, plan for tomorrow.",
      "Still waters run deep.",
      "You can’t have your cake and eat it too.",
    ];
  };

  // Update the initial loading useEffect:
  useEffect(() => {
    setIsLoading(true);
    const scrambledSentences = getSentences();
    const shuffledSentences = shuffleArray(scrambledSentences);
    setSentences(shuffledSentences);
    setIsLoading(false);
  }, []);

  const formatSentence = (sentence) => {
    // Remove punctuation and convert to lowercase
    const cleanedSentence = sentence.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, "").toLowerCase();
    // Split into words, shuffle, and join back into a string
    const words = cleanedSentence.split(" ");
    const shuffledWords = shuffleArray(words);
    return shuffledWords.join(" ");
  };

  useEffect(() => {
    let sentenceInterval;
    if (isPlaying && sentences.length > 0 && !isLoading) {
      // Store the original sentence in displayedSentences
      setDisplayedSentences((prev) => [...new Set([...prev, sentences[sentenceIndex]])]);
      // Show scrambled version on screen
      const formattedSentence = formatSentence(sentences[sentenceIndex]);
      setCurrentSentence(formattedSentence);
      
      sentenceInterval = setInterval(() => {
        setSentenceIndex((prevIndex) => {
          const newIndex = (prevIndex + 1) % sentences.length;
          // Store the original sentence in displayedSentences
          setDisplayedSentences((prev) => [...new Set([...prev, sentences[newIndex]])]);
          // Show scrambled version on screen
          const newFormattedSentence = formatSentence(sentences[newIndex]);
          setCurrentSentence(newFormattedSentence);
          return newIndex;
        });
      }, sentenceChangeInterval);
    } else if (!isPlaying && hasStartedOnce && !isLoading) {
      setCurrentSentence(formatSentence(sentences[sentenceIndex] || 'Play'));
    } else if (!hasStartedOnce || isLoading) {
      setCurrentSentence('Play');
    }
    return () => clearInterval(sentenceInterval);
  }, [isPlaying, sentences, isLoading, sentenceIndex, hasStartedOnce, sentenceChangeInterval]);

  useEffect(() => {
    let interval;
    if (isPlaying && time > 0) {
      interval = setInterval(() => {
        setTime((prevTime) => {
          const newTime = prevTime - 0.1;
          // Calculate progress based on initial duration
          setProgress(100 - (newTime / initialDuration) * 100);
          return newTime > 0 ? newTime : 0;
        });
      }, 100);
    } else if (time === 0) {
      setIsPlaying(false);
    }
    return () => clearInterval(interval);
  }, [isPlaying, time, initialDuration]);

  useEffect(() => {
    let timeout;
    if (isPlaying) {
      const steps = [
        [stick1, stick1], // Pair of sticks for Step 1
        [stick2, stick2], // Pair of sticks for Step 2
        [stick3, stick3],
        [stick4, stick4],
        [stick5, stick5],
        [stick6, stick6],
        [stick7, stick7],
      ];
      const durations = [long, long, long, short, short, short, short];

      const fadeInNextCircle = () => {
        timeout = setTimeout(() => {
          setCurrentStep(nextStep);
          
          if (nextStep === 1) {
            setIsTransitioningToOne(true);
            setFadeInStyle({ opacity: 1, transition: `opacity 0.2s ease-in` });
            setFadeOutStyle({ opacity: 0, transition: `opacity 0.2s ease-out` });
          } else {
            setIsTransitioningToOne(false);
            setFadeInStyle({ opacity: 1, transition: `opacity ${durations[nextStep - 1]}s ease-in` });
          }

          timeout = setTimeout(() => {
            setPreviousStep(null);
            timeout = setTimeout(() => {
              setNextStep((prev) => (prev === 7 ? 1 : prev + 1));
              setPreviousStep(currentStep);
              fadeInNextCircle();
            }, hold * 1000);
          }, nextStep === 1 ? 200 : durations[nextStep - 1] * 1000);
        }, 10);
      };

      fadeInNextCircle();
    }

    return () => clearTimeout(timeout);
  }, [isPlaying, nextStep, long, short, hold, currentStep]);

  useEffect(() => {
    if (time <= 0) {
      setIsPlaying(false);
      setShowCurtain(true);
      setTimeout(() => {
        console.log('Displayed sentences:', displayedSentences);
        const selectedDisplayedSentences = shuffleArray([...displayedSentences]).slice(0, 2);
        const unusedSentences = sentences.filter(sentence => !displayedSentences.includes(sentence));
        const selectedUnusedSentences = shuffleArray(unusedSentences).slice(0, 2);
        
        navigate('/exercise5Test', {
          state: {
            duration: duration,
            errorRate: errorRate,
            selectedDisplayedSentences,
            selectedUnusedSentences,
            timeSpent: timeRef.current
          }
        });
      }, 2000);
    }
  }, [time, navigate, displayedSentences, sentences]);

  useEffect(() => {
    let timer;
    if (isPlaying) {
      timer = setInterval(() => {
        timeRef.current += 1;
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isPlaying]);

  const startBreathing = () => {
    if (!isLoading && sentences.length > 0) {
      setIsPlaying(true);
      setNextStep(2);
      setFadeInStyle({ opacity: 1 });
      setShowPlayIcon(false);
      setShowStopIcon(false);
      if (!hasStartedOnce) {
        setHasStartedOnce(true);
        setSentenceIndex(0);
      }
      setCurrentSentence(sentences[sentenceIndex]);
    }
  };

  const stopBreathing = () => {
    setIsPlaying(false);
    setShowStopIcon(true);
    setInstructionText('Pause');
    setCurrentStep(1);
    setNextStep(2);
    setPreviousStep(null);
    setFadeOutStyle({ opacity: 0 });
    setFadeInStyle({ opacity: 1 });
  };

  const resumeBreathing = () => {
    setIsPlaying(true);
    setShowStopIcon(false);
  };

  const handleEdit = () => {
    navigate('/RhythmChoosing', { state: { 
      returnTo: '/exercise5workout',
      duration:duration,
      time: time
    } });
  };

  const handleClose = () => {
    navigate('/');
  };

  // Function to change word speed
  const changeWordSpeed = (newInterval) => {
    setWordChangeInterval(newInterval);
  };

  // Spacebar feature
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code === "Space") {
        setIsPressed(true);
        setErrorRate((prevError) => {
          const newError = prevError + 1;
          console.log("Spacebar pressed. New error rate:", newError);
          return newError;
        });
      }
    };

    const handleKeyUp = (event) => {
      if (event.code === "Space") {
        setIsPressed(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  return (
    <div className="exercise-container">
      {showCurtain && <Curtain appear={true} duration={2000} />}
      <div className="header-bar">
        <div className="close-icon-wrapper">
          <img src={CloseIcon} alt="close icon" className="close-icon" onClick={handleClose} />
        </div>
        <div className="progress-bar-container">
          <div className="progress-bar">
            <div className="progress" style={{ width: `${progress}%` }}></div>
          </div>
          <span>{`${Math.floor(time / 60)}:${String(Math.floor(time % 60)).padStart(2, '0')}`}</span>
        </div>
        <div className="button-container">
          <button className="save-button" onClick={handleEdit}>Edit Rhythm</button>
        </div>
      </div>

      <div className="word-display">
        <h2>{currentSentence}</h2>
      </div>

      <div className="main-content">
        <div className="circle-container">
          <div className="circle-wrapper">
          {[
              [stick1, stick1],
              [stick2, stick2],
              [stick3, stick3],
              [stick4, stick4],
              [stick5, stick5],
              [stick6, stick6],
              [stick7, stick7],
            ].map((StickPair, index) => (
              <div key={index} className="stick-container">
                {/* Left Stick */}
              <img
                src={StickPair[0]}
                alt={`stick left step ${index + 1}`}
                className="breathing-stick left-stick"
                style={
                  currentStep === index + 1
                    ? fadeInStyle
                    : previousStep === index + 1 ||
                      (index === 0 && currentStep === 2)
                    ? index === 6 && isTransitioningToOne
                      ? fadeOutStyle
                      : { opacity: 1 }
                    : { opacity: 0 }
                }
              />
                {/* Right Stick */}
              <img
                src={StickPair[1]}
                alt={`stick right step ${index + 1}`}
                className="breathing-stick right-stick"
                style={
                  currentStep === index + 1
                    ? fadeInStyle
                    : previousStep === index + 1 ||
                      (index === 0 && currentStep === 2)
                    ? index === 6 && isTransitioningToOne
                      ? fadeOutStyle
                      : { opacity: 1 }
                    : { opacity: 0 }
                }
              />
              </div>
            ))}
            {[person1, person2, person3, person4, person5, person6, person7].map((PersonImg, index) => (
              <img
                key={`person-${index}`}
                src={PersonImg}
                alt={`person step ${index + 1}`}
                className="breathing-circle"
                style={{
                  position: 'absolute',
                  height: '55%',
                  width: 'auto',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  ...(currentStep === index + 1
                    ? fadeInStyle
                    : previousStep === index + 1 || (index === 0 && currentStep === 2)
                    ? index === 6 && isTransitioningToOne ? fadeOutStyle : { opacity: 1 }
                    : { opacity: 0 }),
                  cursor: 'pointer',
                  pointerEvents: currentStep === index + 1 ? 'auto' : 'none'
                }}
                onClick={() => {
                  if (currentStep === index + 1) {
                    stopBreathing();
                  }
                }}
              />
            ))}
            {showPlayIcon && (
              <img
                src={PlayIcon}
                alt="play icon"
                className="play-icon"
                onClick={startBreathing}
              />
            )}
            {showStopIcon && (
              <img
                src={StopIcon}
                alt="stop icon"
                className="play-icon"
                style={{ opacity: 1 }}
                onClick={resumeBreathing}
              />
            )}
          </div>
        </div>

        <div className="breathing-controls">
          <div className="control">
            <div className='row2'>
              <span>{instructionText}</span>
            </div>
          </div>
        </div>
      </div>
      <div className={`spacebar-btn ${isPressed ? "pressed" : ""}`}>
                  SPACE
                </div>

      <div className="instruction-box">
        <img src={InfoIcon} alt="info icon" className="instruction-icon" />
        <ul>
                    <li>Rhythmically breathe while inhaling, holding, exhaling, and holding again. Repeat this cycle.</li>
                    <li>Press the spacebar whenever you feel that you've made a mistake in word comprehension.</li>
                  </ul>
      </div>

    </div>
  );
}

export default Exercise5Workout;